import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ProductCard from "../ProductCard/ProductCard";
import { Product } from "../../types/Product";
import { HttpCategoryProduct } from "../../http";

interface RelatedProductsCarouselProps {
  currentProductId?: string;
  categoryId?: string;
  relatedProductsAmount?: number;
}

const RelatedProductsCarousel: React.FC<RelatedProductsCarouselProps> = ({ 
  currentProductId, 
  categoryId,
  relatedProductsAmount = 8
}) => {
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        if (categoryId) {
          const productsData = await HttpCategoryProduct.getProductsByCategoryId(categoryId, 1, relatedProductsAmount);
          
          // Filter out the current product if provided
          const filteredProducts = productsData.paginaProductosDTO.lista.filter(
            product => product.id !== currentProductId
          );
          
          // Use all filtered products (up to relatedProductsAmount)
          setRelatedProducts(filteredProducts);
        } else {
          setRelatedProducts([]);
        }
      } catch (err) {
        console.error("Error fetching related products:", err);
        setError("Error al cargar productos relacionados");
        setRelatedProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [currentProductId, categoryId, relatedProductsAmount]);

  if (loading) {
    return (
      <Box sx={{ pb: 6, position: 'relative' }}>
        <Box sx={{ p: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: 40,
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Productos Relacionados
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || relatedProducts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ pb: 6, position: 'relative' }}>
      <Box sx={{ p: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: 40,
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          Productos Relacionados
        </Typography>
      </Box>
      
      {/* Carousel */}
      <Box sx={{ position: "relative" }}>
        {/* Custom Navigation Carets */}
        <Box 
          className="swiper-button-prev-related"
          sx={(theme) => ({
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            '&::after': {
              content: '"‹"',
              fontSize: 34,
            }
          })}
        />
        <Box 
          className="swiper-button-next-related"
          sx={(theme) => ({
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            '&::after': {
              content: '"›"',
              fontSize: 34,
            }
          })}
        />
        
        <Swiper
          slidesPerView={4}
          spaceBetween={0}
          navigation={{
            nextEl: ".swiper-button-next-related",
            prevEl: ".swiper-button-prev-related",
          }}
          pagination={{
            clickable: true,
            el: ".related-pagination",
            bulletClass: 'swiper-pagination-bullet',
            bulletActiveClass: 'swiper-pagination-bullet-active',
          }}
          modules={[Navigation, Pagination]}
          loop={relatedProducts.length >= 4}
          breakpoints={{
            0: {
              slidesPerView: 1,
            },
            600: {
              slidesPerView: 2,
            },
            960: {
              slidesPerView: 3,
            },
            1280: {
              slidesPerView: 4,
            },
          }}
          style={{ width: "100%", padding: '0 0' }}
        >
          {relatedProducts.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCard 
                product={product} 
                variant="carousel-border" 
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {relatedProducts.length > 4 && (
        <Box
          className="related-pagination"
          sx={(theme) => ({
            display: "flex",
            justifyContent: "center",
            mt: 4,
            "& .swiper-pagination-bullet": {
              backgroundColor: theme.palette.text.disabled,
              transition: 'background-color 0.3s ease-in-out',
            },
            "& .swiper-pagination-bullet-active": {
              backgroundColor: theme.palette.primary.main,
            },
          })}
        />
      )}
    </Box>
  );
};

export default RelatedProductsCarousel;















